import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
} from '@mui/material';
import { ArrowDownWideNarrow, ArrowUpWideNarrow } from 'lucide-react';
import { ChatLogsT } from '../../types/chat';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface ChatLogsTableProps {
  chatLog: ChatLogsT[];
  onSelect: (chatLog: ChatLogsT) => void;
}

export default function ChatLogsTable({ chatLog, onSelect }: ChatLogsTableProps) {
  const theme = useTheme();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ChatLogsT;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [sortingIcon, setSortingIcon] = useState<React.ReactNode>(
    <ArrowDownWideNarrow size={16} />
  );
  const bookings = useSelector((state: RootState) => state.bookings.items);
  const commonbooking = bookings.filter(booking =>
    chatLog.some(chatlog => booking.connectionId === chatlog.uuid)
  );

  const sortedChatLog = React.useMemo(() => {
    let sortableItems = [...chatLog];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [chatLog, sortConfig]);

  const requestSort = (key: keyof ChatLogsT) => {
    let direction: 'asc' | 'desc' = 'asc';
    setSortingIcon(<ArrowUpWideNarrow size={16} />);
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
      setSortingIcon(<ArrowDownWideNarrow size={16} />);
    }
    setSortConfig({ key, direction });
  };

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>UID</TableCell>
              <TableCell
                sx={{ px: 3, py: 1, color: theme.palette.text.secondary, cursor: 'pointer' }}
                onClick={() => requestSort('updatedAt')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Date {sortingIcon}</Box>
              </TableCell>
              <TableCell sx={{ px: 3, py: 1, color: theme.palette.text.secondary }}>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedChatLog.map(chatLog => {
              return (
                <TableRow
                  key={chatLog.uuid}
                  hover
                  onClick={() => onSelect(chatLog)}
                  sx={{
                    cursor: 'pointer',
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: !!chatLog.isViewed
                      ? 'inherit'
                      : `${theme.palette.primary.main}12`,
                  }}
                >
                  <TableCell
                    sx={{
                      px: 3,
                      fontSize: 15,
                      fontWeight: !!chatLog.isViewed ? 400 : 500,
                    }}
                  >
                    {chatLog.uuid}
                  </TableCell>
                  <TableCell sx={{ px: 3, fontWeight: 400 }}>
                    {format(new Date(chatLog.updatedAt || Date.now()), 'MMM d, yyyy, h:mm a')}
                  </TableCell>
                  <TableCell sx={{ px: 3, fontWeight: 400 }}>
                    {chatLog.uuid ===
                    commonbooking.find(item => item.connectionId)?.connectionId ? (
                      <Chip
                        label="Booking"
                        size="small"
                        sx={{
                          backgroundColor: `${theme.palette.primary.main}10`,
                          color: theme.palette.primary.main,
                        }}
                      />
                    ) : (
                      'Enquiry'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
